import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useShuddhoshikhaContext } from '../hooks/useShuddhoshikhaContext';
import { normalizeAjaxUrl } from '../utils/normalizeUrl';
import { 
  Flame, 
  Calendar, 
  Bell, 
  ArrowRight,
  Sparkles,
  Search,
  CheckCircle2
} from 'lucide-react';
import ProfileCompareView from '../components/practice/ProfileCompareView';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

interface HomeViewProps {
  onViewChange?: (view: string) => void;
  onSelectCourse?: (courseId: number) => void;
  onNotificationOpen?: () => void;
  subscriptionTier?: 'free' | 'pro' | 'max';
  unreadCount?: number;
  isDarkMode?: boolean;
}

const toBengaliDigits = (num: number | string) => {
  const bengaliDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().replace(/\d/g, (digit) => bengaliDigits[parseInt(digit)]);
};

export default function HomeView({ onViewChange, onSelectCourse, onNotificationOpen, subscriptionTier, unreadCount = 0, isDarkMode = false }: HomeViewProps) {
  const { profile } = useShuddhoshikhaContext();
  const [courseFilter, setCourseFilter] = useState<'all' | 'ongoing'>('all');
  const [coursesList, setCoursesList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Monitor screen width to auto-switch layouts
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true);
        const rawAjaxUrl = (window as any).sh_react_object?.ajax_url || 'https://shuddhoshiksha.com/wp-admin/admin-ajax.php';
        const ajaxUrl = normalizeAjaxUrl(rawAjaxUrl);
        const params = new URLSearchParams();
        params.append('action', 'sh_get_react_courses');
        
        const response = await fetch(ajaxUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(),
        });
        
        const data = await response.json();
        if (data.success) {
          setCoursesList(data.data || []);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const userEnrolledIds = profile?.enrolled_courses || (window as any).shAuth?.user?.enrolled_courses || [];
  const enrolledCourses = coursesList.filter(c => userEnrolledIds.includes(Number(c.id)));

  const points = profile?.stats?.points ?? 0;
  const exams = profile?.stats?.exams ?? 9;
  const streak = profile?.stats?.streak ?? 2;

  // Shuddhoshikha dynamic stats
  const completionPercent = exams > 0 ? Math.min(100, Math.round((exams / 12) * 100)) : 30;
  const strokeDashoffset = 282.7 * (1 - completionPercent / 100);
  const successRate = points > 0 && exams > 0 ? Math.min(95, Math.round((points / (exams * 30)) * 100)) : 65;

  // Dynamic streak days calculation based on today and streak count
  const todayDayOfWeek = new Date().getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const weekdaysConfig = [
    { label: 'সোম', value: 1 },
    { label: 'মঙ্গল', value: 2 },
    { label: 'বুধ', value: 3 },
    { label: 'বৃহস্পতি', value: 4 },
    { label: 'শুক্র', value: 5 },
    { label: 'শনি', value: 6 },
    { label: 'রবি', value: 0 },
  ];
  const dynamicStreakDays = weekdaysConfig.map((day) => {
    let isActive = false;
    if (streak > 0) {
      let diff = todayDayOfWeek - day.value;
      if (diff < 0) diff += 7;
      isActive = diff < streak;
    }
    return {
      label: day.label,
      active: isActive,
    };
  });

  const filteredCourses = courseFilter === 'all' 
    ? enrolledCourses 
    : enrolledCourses.filter(c => (c.progress ?? 0) > 0 && (c.progress ?? 0) < 100);

  if (isMobile) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6 max-w-3xl mx-auto w-full px-4 sm:px-6 md:px-8 py-4 text-left"
      >
        {/* 1. User Profile & Progress (rendered via ProfileCompareView) */}
        <motion.div variants={itemVariants} className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-4 sm:p-5 md:p-6 border border-gray-100 shadow-sm animate-fadeIn">
          <ProfileCompareView
            targetUserId={(window as any).shAuth?.user?.id || 0}
            isDarkMode={isDarkMode}
          />
        </motion.div>

        {/* 2. Spotlight Ad Card */}
        <motion.div
          variants={itemVariants}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#00A3FF] to-blue-700 dark:from-[#8AB4F8]/15 dark:to-blue-950/45 p-6 flex flex-col justify-between text-white border border-blue-600/20 dark:border-white/10 shadow-sm min-h-[160px]"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full blur-xl pointer-events-none" />
          <div className="space-y-1 z-10 text-left">
            <div className="flex items-center gap-2 mb-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              <span className="text-[9px] bg-white/20 dark:bg-white/10 px-2 py-0.5 rounded-full font-bold">১০MS SPOTLIGHT</span>
            </div>
            <h4 className="font-bangla text-base font-extrabold leading-tight">ভর্তি হয়ে প্রস্তুতি শুরু করো</h4>
            <p className="font-bangla text-[10px] text-blue-100 dark:text-[#A0A5AD]">পরীক্ষার মডিউল ও লাইভ সリューション ক্লাস</p>
          </div>
          <button 
            onClick={() => onViewChange?.('কোর্সসমূহ')}
            className="self-start mt-4 bg-white dark:bg-[#8AB4F8] text-blue-700 dark:text-[#121212] hover:bg-blue-50 dark:hover:bg-[#729ee6] font-bangla text-[10px] font-bold px-4 py-2 rounded-xl shadow dark:shadow-[0_0_15px_rgba(138,180,248,0.25)] transition-all cursor-pointer border-none"
          >
            কোর্স দেখতে ক্লিক করুন
          </button>
        </motion.div>

        {/* 3. Streak / Activity Tracker */}
        <motion.div variants={itemVariants} className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] rounded-3xl p-5 border border-gray-100 shadow-sm space-y-4 transition-colors duration-300">
          <div className="flex items-center justify-between">
            <h4 className="font-bangla text-base font-extrabold text-slate-800 dark:text-[#E8EAED] flex items-center gap-1.5 animate-fadeIn">
              <Flame className="w-5 h-5 text-orange-500 fill-orange-500 animate-pulse" />
              {toBengaliDigits(streak)} দিনের ধারাবাহিকতা!
            </h4>
          </div>
          <p className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD]">প্রতিদিন পড়ালেখা করো স্ট্রিক ধরে রাখতে</p>
          <div className="grid grid-cols-7 gap-2 text-center pt-2">
            {dynamicStreakDays.map((day, idx) => (
              <div key={idx} className="space-y-2 flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all ${
                  day.active 
                    ? 'bg-gradient-to-br from-orange-400 to-amber-500 border-transparent shadow-md shadow-orange-500/20 text-white' 
                    : 'bg-gray-50 dark:bg-white/5 border-gray-100 dark:border-white/5 text-gray-400 dark:text-[#A0A5AD]'
                }`}>
                  {day.active ? (
                    <Flame className="w-4 h-4 fill-white text-white" />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
                  )}
                </div>
                <span className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD] font-semibold">{day.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* 4. অনুশীলন circular widget */}
        <motion.div variants={itemVariants} className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] rounded-3xl p-5 border border-gray-100 shadow-sm space-y-5 relative overflow-hidden transition-colors duration-300">
          <div className="absolute top-0 right-0 w-24 h-24 bg-rose-500/5 rounded-full blur-2xl pointer-events-none" />
          <div className="flex items-center justify-between border-b border-gray-50 dark:border-white/5 pb-3">
            <span className="font-bangla font-black text-base text-[#1F1F1F] dark:text-[#E8EAED] flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-rose-500 fill-rose-500" />
              অনুশীলন
            </span>
            <span className="font-bangla text-[8px] bg-rose-50 dark:bg-[#8AB4F8]/10 text-rose-500 dark:text-[#8AB4F8] font-bold px-2 py-0.5 rounded-full border border-rose-100 dark:border-[#8AB4F8]/20">
              NEW
            </span>
          </div>

          <div className="flex flex-col items-center justify-center py-2 space-y-2">
            <div className="relative w-28 h-28 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="56"
                  cy="56"
                  r="45"
                  stroke="#F1F5F9"
                  strokeWidth="8"
                  fill="transparent"
                  className="dark:stroke-white/5"
                />
                <circle
                  cx="56"
                  cy="56"
                  r="45"
                  stroke="#00CBA9"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray="282.7"
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  className="transition-all duration-1000 dark:stroke-[#8AB4F8]"
                />
              </svg>
              <div className="absolute flex flex-col items-center justify-center">
                <span className="font-bangla text-xl font-black text-[#1F1F1F] dark:text-[#E8EAED]">{toBengaliDigits(completionPercent)}%</span>
                <span className="font-bangla text-[9px] text-gray-400 dark:text-[#A0A5AD] mt-0.5 font-medium">সিলেবাস শেষ</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-2 text-xs font-bangla font-semibold text-gray-600 dark:text-[#A0A5AD]">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-rose-500 dark:text-[#8AB4F8]/80" /> আইডেন্টিফাইড উইকনেস</span>
              <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-[#00A3FF] dark:text-[#8AB4F8]/80" /> প্র্যাকটিস করা চ্যাপ্টার</span>
              <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(Math.min(12, exams))}/১২</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-amber-500 dark:text-amber-400" /> কুইজ স্কোর</span>
              <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">
                {toBengaliDigits(points)}/{toBengaliDigits(Math.max(240, Math.ceil(points / 40) * 40))}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400" /> ডেইলি স্ট্রিক</span>
              <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(streak)} দিন</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-purple-500 dark:text-purple-400" /> সাফল্যের হার</span>
              <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(successRate)}%</span>
            </div>
          </div>

          <button 
            onClick={() => onViewChange?.('চর্চাঘর')}
            className="w-full py-3 bg-[#00CBA9] dark:bg-[#8AB4F8] hover:bg-[#00b898] dark:hover:bg-[#729ee6] text-white dark:text-[#121212] font-bangla text-xs font-bold rounded-2xl transition-all shadow-md shadow-[#00CBA9]/15 dark:shadow-[0_0_15px_rgba(138,180,248,0.25)] cursor-pointer border-none"
          >
            অ্যাক্টিভিটি শুরু
          </button>
        </motion.div>

        {/* 5. "তোমার কোর্সসমূহ" Card List */}
        <motion.div variants={itemVariants} className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">তোমার কোর্সসমূহ</h3>
            <button 
              onClick={() => onViewChange?.('কোর্সসমূহ')}
              className="font-bangla text-xs text-gray-400 dark:text-[#A0A5AD] hover:text-[#00CBA9] dark:hover:text-[#8AB4F8] flex items-center gap-0.5 transition-colors font-medium cursor-pointer"
            >
              সব দেখো <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Filter Toggle Buttons */}
          <div className="flex gap-2 pb-2">
            <button
              onClick={() => setCourseFilter('all')}
              className={`font-bangla px-4 py-1.5 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                courseFilter === 'all'
                  ? 'bg-[#1F1F1F] dark:bg-[#8AB4F8] text-white dark:text-[#121212] border-[#1F1F1F] dark:border-[#8AB4F8] shadow-sm'
                  : 'bg-white dark:bg-[#1E1E1E]/50 text-gray-500 dark:text-[#A0A5AD] border-gray-100 dark:border-white/10 hover:border-gray-200'
              }`}
            >
              সব
            </button>
            <button
              onClick={() => setCourseFilter('ongoing')}
              className={`font-bangla px-4 py-1.5 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                courseFilter === 'ongoing'
                  ? 'bg-[#1F1F1F] dark:bg-[#8AB4F8] text-white dark:text-[#121212] border-[#1F1F1F] dark:border-[#8AB4F8] shadow-sm'
                  : 'bg-white dark:bg-[#1E1E1E]/50 text-gray-500 dark:text-[#A0A5AD] border-gray-100 dark:border-white/10 hover:border-gray-200'
              }`}
            >
              চলমান ({enrolledCourses.filter(c => (c.progress ?? 0) > 0 && (c.progress ?? 0) < 100).length})
            </button>
          </div>

          {/* Course list */}
          <div className="space-y-3">
            {isLoading ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2].map((n) => (
                  <div key={n} className="h-20 bg-slate-50 dark:bg-zinc-900/40 border border-slate-100 dark:border-zinc-800/80 rounded-2xl" />
                ))}
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-2xl border border-gray-100 p-8 text-center space-y-4">
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">
                  আপনি এখনো কোনো কোর্সে ভর্তি হননি।
                </p>
                <button
                  onClick={() => onViewChange?.('কোর্সসমূহ')}
                  className="bg-[#00CBA9] dark:bg-[#8AB4F8] hover:bg-[#00b294] text-white dark:text-[#121212] font-bangla text-xs font-bold px-5 py-2.5 rounded-xl transition-all cursor-pointer border-none"
                >
                  কোর্স দেখতে ক্লিক করুন
                </button>
              </div>
            ) : (
              filteredCourses.map((course) => (
                <div 
                  key={course.id} 
                  className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-2xl border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-sm transition-all"
                >
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <h4 className="font-bangla text-sm font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{course.title}</h4>
                      <span className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD] font-semibold">{(course.progress ?? 0)}% সম্পন্ন</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-50 dark:bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 dark:from-[#8AB4F8]/80 dark:to-[#8AB4F8] rounded-full" style={{ width: `${(course.progress ?? 0)}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button 
                      onClick={() => onSelectCourse?.(course.id)}
                      className="w-full sm:w-auto px-4 py-2 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-100 dark:border-white/10 text-gray-700 dark:text-[#E8EAED] font-bangla text-xs font-semibold rounded-xl transition-colors cursor-pointer"
                    >
                      পড়াশোনা শুরু
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Search and Header Top bar */}
      <motion.div variants={itemVariants} className="flex items-center justify-between gap-4">
        {/* Search Bar */}
        <div className="relative flex-1 max-w-md hidden md:block">
          <input
            type="text"
            placeholder="কোর্স সার্চ করুন..."
            className="w-full bg-white dark:bg-[#1E1E1E]/50 border border-gray-100 dark:border-white/10 rounded-2xl pl-10 pr-4 py-2.5 text-xs font-bangla dark:text-[#E8EAED] focus:outline-none focus:ring-1 focus:ring-[#00CBA9] dark:focus:ring-[#8AB4F8] focus:border-transparent transition-all"
          />
          <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-400 dark:text-[#A0A5AD]" />
        </div>

        {/* Date and Notification */}
        <div className="flex items-center gap-3 ml-auto">
          {subscriptionTier === 'free' && (
            <button
              onClick={() => onViewChange?.('সাবস্ক্রিপশন')}
              className="hidden lg:flex items-center gap-2 px-4 py-2 bg-[#0B57D0] hover:bg-[#0842A0] text-white font-bangla text-sm font-semibold rounded-full border border-blue-400/30 shadow-md active:scale-95 transition-all cursor-pointer"
            >
              <Sparkles className="w-4 h-4 text-[#C2E7FF] fill-current" />
              আপগ্রেড
            </button>
          )}

          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1E1E1E]/50 rounded-2xl border border-gray-100 dark:border-white/10 shadow-sm transition-colors duration-300">
            <Calendar className="w-4 h-4 text-[#00CBA9] dark:text-[#8AB4F8]" />
            <span className="font-bangla text-xs text-gray-700 dark:text-[#E8EAED]">{new Date().toLocaleDateString('bn-BD')}</span>
          </div>
          <button
            onClick={onNotificationOpen}
            className="hidden lg:flex w-10 h-10 rounded-2xl bg-white dark:bg-[#1E1E1E]/50 border border-gray-100 dark:border-white/10 items-center justify-center hover:bg-gray-50 dark:hover:bg-white/5 transition-colors shadow-sm cursor-pointer text-gray-600 dark:text-[#A0A5AD] relative"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-black flex items-center justify-center rounded-full px-1 border-2 border-white dark:border-[#121212]">
                {unreadCount}
              </span>
            )}
          </button>
        </div>
      </motion.div>

      {/* Main Grid: Left Column (Profile & Courses), Right Column (Spotlight, Streak & Practice) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column (Spans 8 cols) */}
        <div className="lg:col-span-8 space-y-8">
          {/* Promoted primary focus Progress component replacing the welcome banner */}
          <motion.div
            variants={itemVariants}
            className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-6 border border-gray-100 shadow-sm animate-fadeIn"
          >
            <ProfileCompareView
              targetUserId={(window as any).shAuth?.user?.id || 0}
              isDarkMode={isDarkMode}
            />
          </motion.div>
          
          {/* "তোমার কোর্সসমূহ" Card List */}
          <motion.div variants={itemVariants} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">তোমার কোর্সসমূহ</h3>
              <button 
                onClick={() => onViewChange?.('কোর্সসমূহ')}
                className="font-bangla text-xs text-gray-400 dark:text-[#A0A5AD] hover:text-[#00CBA9] dark:hover:text-[#8AB4F8] flex items-center gap-0.5 transition-colors font-medium cursor-pointer"
              >
                সব দেখো <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Filter Toggle Buttons */}
            <div className="flex gap-2 pb-2">
              <button
                onClick={() => setCourseFilter('all')}
                className={`font-bangla px-4 py-1.5 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                  courseFilter === 'all'
                    ? 'bg-[#1F1F1F] dark:bg-[#8AB4F8] text-white dark:text-[#121212] border-[#1F1F1F] dark:border-[#8AB4F8] shadow-sm dark:shadow-[0_0_10px_rgba(138,180,248,0.2)]'
                    : 'bg-white dark:bg-[#1E1E1E]/50 text-gray-500 dark:text-[#A0A5AD] border-gray-100 dark:border-white/10 hover:border-gray-200 dark:hover:border-white/20'
                }`}
              >
                সব
              </button>
              <button
                onClick={() => setCourseFilter('ongoing')}
                className={`font-bangla px-4 py-1.5 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                  courseFilter === 'ongoing'
                    ? 'bg-[#1F1F1F] dark:bg-[#8AB4F8] text-white dark:text-[#121212] border-[#1F1F1F] dark:border-[#8AB4F8] shadow-sm dark:shadow-[0_0_10px_rgba(138,180,248,0.2)]'
                    : 'bg-white dark:bg-[#1E1E1E]/50 text-gray-500 dark:text-[#A0A5AD] border-gray-100 dark:border-white/10 hover:border-gray-200 dark:hover:border-white/20'
                }`}
              >
                চলমান ({enrolledCourses.filter(c => (c.progress ?? 0) > 0 && (c.progress ?? 0) < 100).length})
              </button>
            </div>

            {/* Course Rows */}
            <div className="space-y-3">
              {isLoading ? (
                <div className="space-y-3 animate-pulse">
                  {[1, 2].map((n) => (
                    <div key={n} className="h-20 bg-slate-50 dark:bg-zinc-900/40 border border-slate-100 dark:border-zinc-800/80 rounded-2xl p-4 flex items-center justify-between" />
                  ))}
                </div>
              ) : filteredCourses.length === 0 ? (
                <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-2xl border border-gray-100 p-8 text-center space-y-4 shadow-sm">
                  <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">
                    আপনি এখনো কোনো কোর্সে ভর্তি হননি।
                  </p>
                  <button
                    onClick={() => onViewChange?.('কোর্সসমূহ')}
                    className="bg-[#00CBA9] dark:bg-[#8AB4F8] hover:bg-[#00b294] text-white dark:text-[#121212] font-bangla text-xs font-bold px-5 py-2.5 rounded-xl transition-all cursor-pointer border-none"
                  >
                    কোর্স দেখতে ক্লিক করুন
                  </button>
                </div>
              ) : (
                filteredCourses.map((course) => (
                  <div 
                    key={course.id} 
                    className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] rounded-2xl border border-gray-100 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:shadow-sm dark:hover:shadow-[#8AB4F8]/5 transition-all shadow-sm"
                  >
                    <div className="flex-1 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <h4 className="font-bangla text-sm md:text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{course.title}</h4>
                        <span className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD] font-semibold">{(course.progress ?? 0)}% সম্পন্ন</span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full h-1.5 bg-gray-50 dark:bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 dark:from-[#8AB4F8]/80 dark:to-[#8AB4F8] rounded-full" style={{ width: `${(course.progress ?? 0)}%` }} />
                      </div>

                      {/* Instructors Avatars */}
                      <div className="flex items-center gap-1.5 pt-0.5">
                        <span className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD] mr-2">মেন্টরস:</span>
                        <div className="flex -space-x-1.5">
                          <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-[#8AB4F8]/20 border-2 border-white dark:border-[#1E1E1E] flex items-center justify-center text-[9px] font-bold text-blue-700 dark:text-[#8AB4F8]">রা</div>
                          <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-500/20 border-2 border-white dark:border-[#1E1E1E] flex items-center justify-center text-[9px] font-bold text-purple-700 dark:text-purple-400">ইম</div>
                          <div className="w-6 h-6 rounded-full bg-teal-100 dark:bg-emerald-500/20 border-2 border-white dark:border-[#1E1E1E] flex items-center justify-center text-[9px] font-bold text-teal-700 dark:text-emerald-400">তান</div>
                        </div>
                      </div>
                    </div>

                    {/* Right Button actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {(course.progress ?? 0) >= 90 && (
                        <button className="px-4 py-2 bg-emerald-500 dark:bg-[#8AB4F8]/90 hover:bg-emerald-600 dark:hover:bg-[#8AB4F8] text-white dark:text-[#121212] font-bangla text-xs font-semibold rounded-xl transition-colors shadow-sm cursor-pointer border-none">
                          সার্টিফিকেট
                        </button>
                      )}
                      <button 
                        onClick={() => onSelectCourse?.(course.id)}
                        className="px-4 py-2 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-100 dark:border-white/10 text-gray-700 dark:text-[#E8EAED] font-bangla text-xs font-semibold rounded-xl transition-colors cursor-pointer"
                      >
                        পড়াশোনা শুরু
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>


        </div>

        {/* Right Column (Spans 4 cols) */}
        <div className="lg:col-span-4 space-y-6">
          
          {/* Promo Ad Card (Blue gradient / Glass style in Dark Mode) */}
          <motion.div
            variants={itemVariants}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#00A3FF] to-blue-700 dark:from-[#8AB4F8]/15 dark:to-blue-950/45 p-6 flex flex-col justify-between text-white border border-blue-600/20 dark:border-white/10 shadow-sm transition-colors duration-300 min-h-[160px]"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full blur-xl pointer-events-none" />
            <div className="space-y-1 z-10 text-left">
              <span className="text-[9px] bg-white/20 dark:bg-white/10 px-2 py-0.5 rounded-full font-bold">১০MS SPOTLIGHT</span>
              <h4 className="font-bangla text-base font-extrabold leading-tight">ভর্তি হয়ে প্রস্তুতি শুরু করো</h4>
              <p className="font-bangla text-[10px] text-blue-100 dark:text-[#A0A5AD]">পরীক্ষার মডিউল ও লাইভ সলিউশন ক্লাস</p>
            </div>
            <button 
              onClick={() => onViewChange?.('কোর্সসমূহ')}
              className="self-start mt-4 bg-white dark:bg-[#8AB4F8] text-blue-700 dark:text-[#121212] hover:bg-blue-50 dark:hover:bg-[#729ee6] font-bangla text-[10px] font-bold px-4 py-2 rounded-xl shadow dark:shadow-[0_0_15px_rgba(138,180,248,0.25)] transition-all cursor-pointer border-none"
            >
              কোর্স দেখতে ক্লিক করুন
            </button>
          </motion.div>
          
          {/* 1. Streak / Activity Tracker */}
          <motion.div variants={itemVariants} className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] rounded-3xl p-5 shadow-sm space-y-4 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <h4 className="font-bangla text-base font-extrabold text-slate-800 dark:text-[#E8EAED] flex items-center gap-1.5">
                <Flame className="w-5 h-5 text-orange-500 fill-orange-500 animate-pulse" />
                {toBengaliDigits(streak)} দিনের ধারাবাহিকতা!
              </h4>
            </div>
            
            <p className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD]">প্রতিদিন পড়ালেখা করো স্ট্রিক ধরে রাখতে</p>

            {/* Streak Days Row */}
            <div className="grid grid-cols-7 gap-2 text-center pt-2">
              {dynamicStreakDays.map((day, idx) => (
                <div key={idx} className="space-y-2 flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all ${
                    day.active 
                      ? 'bg-gradient-to-br from-orange-400 to-amber-500 border-transparent shadow-md shadow-orange-500/20 text-white' 
                      : 'bg-gray-50 dark:bg-white/5 border-gray-100 dark:border-white/5 text-gray-400 dark:text-[#A0A5AD]'
                  }`}>
                    {day.active ? (
                      <Flame className="w-4 h-4 fill-white text-white" />
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
                    )}
                  </div>
                  <span className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD] font-semibold">{day.label}</span>
                </div>
              ))}
            </div>

            <button className="w-full text-center font-bangla text-xs font-bold text-[#00CBA9] dark:text-[#8AB4F8] hover:underline pt-2 border-t border-gray-50 dark:border-white/5 block cursor-pointer">
              বিস্তারিত স্ট্রিক রিপোর্ট
            </button>
          </motion.div>

          {/* 2. SuperPrep Circular Widget */}
          <motion.div variants={itemVariants} className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] rounded-3xl p-5 shadow-sm space-y-5 relative overflow-hidden transition-colors duration-300">
            <div className="absolute top-0 right-0 w-24 h-24 bg-rose-500/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex items-center justify-between border-b border-gray-50 dark:border-white/5 pb-3">
              <span className="font-bangla font-black text-base text-[#1F1F1F] dark:text-[#E8EAED] flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-rose-500 fill-rose-500" />
                অনুশীলন
              </span>
              <span className="font-bangla text-[8px] bg-rose-50 dark:bg-[#8AB4F8]/10 text-rose-500 dark:text-[#8AB4F8] font-bold px-2 py-0.5 rounded-full border border-rose-100 dark:border-[#8AB4F8]/20">
                NEW
              </span>
            </div>

            {/* Circular Progress Display */}
            <div className="flex flex-col items-center justify-center py-2 space-y-2">
              <div className="relative w-28 h-28 flex items-center justify-center">
                {/* SVG Progress Circle */}
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="56"
                    cy="56"
                    r="45"
                    stroke="#F1F5F9"
                    strokeWidth="8"
                    fill="transparent"
                    className="dark:stroke-white/5"
                  />
                  <circle
                    cx="56"
                    cy="56"
                    r="45"
                    stroke="#00CBA9"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray="282.7"
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="transition-all duration-1000 dark:stroke-[#8AB4F8]"
                  />
                </svg>
                {/* Center text */}
                <div className="absolute flex flex-col items-center justify-center">
                  <span className="font-bangla text-xl font-black text-[#1F1F1F] dark:text-[#E8EAED]">{toBengaliDigits(completionPercent)}%</span>
                  <span className="font-bangla text-[9px] text-gray-400 dark:text-[#A0A5AD] mt-0.5 font-medium">সিলেবাস শেষ</span>
                </div>
              </div>
            </div>

            {/* Analytics Status List */}
            <div className="space-y-3 pt-2 text-xs font-bangla font-semibold text-gray-600 dark:text-[#A0A5AD]">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-rose-500 dark:text-[#8AB4F8]/80" /> আইডেন্টিফাইড উইকনেস</span>
                <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-[#00A3FF] dark:text-[#8AB4F8]/80" /> প্র্যাকটিস করা চ্যাপ্টার</span>
                <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(Math.min(12, exams))}/১২</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-amber-500 dark:text-amber-400" /> কুইজ স্কোর</span>
                <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">
                  {toBengaliDigits(points)}/{toBengaliDigits(Math.max(240, Math.ceil(points / 40) * 40))}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400" /> ডেইলি স্ট্রিক</span>
                <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(streak)} দিন</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="w-4 h-4 text-purple-500 dark:text-purple-400" /> সাফল্যের হার</span>
                <span className="font-bangla font-extrabold text-slate-800 dark:text-[#E8EAED]">{toBengaliDigits(successRate)}%</span>
              </div>
            </div>

            <button 
              onClick={() => onViewChange?.('চর্চাঘর')}
              className="w-full py-3 bg-[#00CBA9] dark:bg-[#8AB4F8] hover:bg-[#00b898] dark:hover:bg-[#729ee6] text-white dark:text-[#121212] font-bangla text-xs font-bold rounded-2xl transition-all shadow-md shadow-[#00CBA9]/15 dark:shadow-[0_0_15px_rgba(138,180,248,0.25)] cursor-pointer border-none"
            >
              অ্যাক্টিভিটি শুরু
            </button>
          </motion.div>

        </div>

      </div>
    </motion.div>
  );
}
